'use client';
// eslint-disable-next-line deprecate/import
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import cn from 'classnames';
import { startCase } from 'lodash';

import { Select } from '@uikit';

import { useAuth } from '@shared/common/providers/AuthProvider';

import type { BlogParams} from '@query/blog';
import { useBlogCategoriesQuery, useBlogListInfiniteQuery } from '@query/blog';

import { ArrowRight } from '@ui/uikit/components/icons/mono/ArrowRight';
import { Sort } from '@ui/uikit/components/icons/mono/Sort';

import { BlogCard, SkeletonList } from './BlogCard';
import { NotFound } from './NotFound';

const Page = ({ type, value, isAuthServer }: BlogParams & { isAuthServer: boolean }) => {
  const { user } = useAuth();
  const [sortOption, setSortOption] = useState('createdAt:desc');

  const {
    data: blogArticles,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending: isLoading,
    isFetchingNextPage,
    refetch,
  } = useBlogListInfiniteQuery(
    { type, value, sort: sortOption },
    !!user?.attributes?.uuid || isAuthServer,
    user?.attributes?.uuid,
  );
  const { data: categories } = useBlogCategoriesQuery();
  const pathname = usePathname();
  const router = useRouter();

  const { ref } = useInView({
    onChange: (inView) =>
      inView && !isError && !isLoading && !isFetchingNextPage && hasNextPage && fetchNextPage(),
  });

  useEffect(() => {
    if (user?.attributes.uuid) {
      refetch();
    }
  }, [user, refetch]);

  const articles = useMemo(() => {
    return blogArticles?.pages?.flatMap((page) => page?.data) || [];
  }, [blogArticles]);

  const categoriesOptions = useMemo(() => {
    const options =
      categories?.data?.map((action) => ({
        value: `/blog/category/${action.slug}`,
        label: action.name,
      })) || [];
    options.unshift({
      value: '/blog',
      label: 'All Topics',
    });
    return options;
  }, [categories?.data]);

  const handleFilterChange = (selectedOption: { value: string; label: string }) => {
    router.push(selectedOption.value);
  };

  const onClick = () =>
    setSortOption((prev) => (prev === 'createdAt:desc' ? 'createdAt:asc' : 'createdAt:desc'));

  return (
    <div className="v2-container">
      {type !== 'tags' ? (
        <>
          <h1 className="text-h1-a mt-10 md:mt-14 pb-3 md:pb-4 text-base-text-primary text-center">
            Magic Square Blog
          </h1>
          <h4 className="text-base-text-tertiary text-caption-md-d text-center">
            Your Guide to the Magical World of Web 3.0
          </h4>
          <div className="hidden md:flex flex-wrap justify-center gap-3 pt-10 md:pt-14 px-[66px]">
            {categoriesOptions.map((category) => (
              <Link
                className={cn(
                  'rounded-xl bg-button-secondary-default-bg hover:bg-button-secondary-hover-bg transition-all text-button-secondary-default-text text-caption-sm-a py-2.5 px-4 whitespace-nowrap',
                  {
                    'text-button-primary-default-text text-sm font-medium !bg-button-primary-default-bg':
                      pathname === category.value,
                  },
                )}
                key={category.value}
                href={category.value}
              >
                {category.label}
              </Link>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap justify-between md:justify-center items-start pt-[32px]">
            <Select
              className="flex md:hidden w-full sm:max-w-[364px] text-base-text-primary text-caption-md-strong-m !border-base-border-medium rounded-xl"
              classNameControll="rounded-lg border-primary-200"
              value={categoriesOptions?.find((option) => option.value === pathname)}
              onChange={(option) => handleFilterChange(option as { value: string; label: string })}
              options={categoriesOptions}
              isSearchable={false}
            />
            <button
              className="inline-flex items-center justify-center w-full sm:w-auto mb-10 md:mb-14 rounded-xl bg-button-secondary-default-bg hover:bg-button-secondary-hover-bg transition-all text-button-secondary-default-text text-caption-sm-d py-2.5 px-4"
              onClick={onClick}
            >
              <Sort className="mr-2 size-3.5" />
              {sortOption === 'createdAt:desc' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </>
      ) : (
        <div className="pb-14 pt-10">
          <ul className="flex justify-start gap-2">
            <li>
              <Link href="/blog" className="text-caption-xs-a text-base-text-tertiary">
                Magic Blog
              </Link>
            </li>
            <li>
              <ArrowRight className="icon-2xs text-caption-xs-a text-base-text-tertiary" />
            </li>
            <li>
              <span className="text-caption-xs-a text-base-text-tertiary">{startCase(value)}</span>
            </li>
          </ul>
          <h2 className="text-h1-a pb-8">
            Posts tagged <span className="text-base-text-pink">{startCase(value)}</span>
          </h2>
          <button
            className="inline-flex items-center justify-center w-full sm:w-auto mb-10 md:mb-14 rounded-xl bg-button-secondary-default-bg text-button-secondary-default-text text-caption-sm-d py-2.5 px-4"
            onClick={onClick}
          >
            <Sort className="mr-2 size-3.5" />
            {sortOption === 'createdAt:desc' ? 'Oldest' : 'Newest'}
          </button>
        </div>
      )}
      {!articles.length && isLoading && <SkeletonList />}
      {!articles.length && !isLoading && <NotFound />}
      {!!articles.length && !isLoading && (
        <section
          className="
            grid gap-2 md:gap-4
            grid-cols-1
            sm:grid-cols-2
            md:grid-cols-2
            lg:grid-cols-3
            xl:grid-cols-4
          "
        >
          {articles &&
            articles.map((article) => (
              <BlogCard key={article.documentId} article={article} updatingKey="blogList" />
            ))}
        </section>
      )}
      {hasNextPage && articles.length && (
        <SkeletonList key={blogArticles?.pages.length} ref={ref} />
      )}
    </div>
  );
};

export default Page;
